"use client";

import { useInspectorMode } from "@utils/ContentfulUtils";
import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import { getImageProps } from "next/image";
import { FC, useCallback, useEffect, useRef, useState, useTransition } from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import "swiper/css";
import "swiper/css/effect-fade";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Autoplay, EffectFade } from "swiper/modules";
import { Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import Image from "@/components/ui/Image/Image";
import { ctaClickTracking, eventSectionTracking } from "@/dataLayer/dataLayer";
import useIsSaleScreen from "@/hooks/useIsSaleScreen";
import cn from "@/utils/cn";
import { countdownToEvent } from "@/utils/countdownToEvent";
import { formatDate } from "@/utils/dateUtils";

import { CountdownProps, EventLaunchCardProps } from "./types";

const Countdown: React.FC<CountdownProps> = ({ eventDate, earlyAccessCta, eventName }) => {
  const timeUntilEvent = countdownToEvent(new Date(eventDate));
  const EventLaunchButtonClasses = cn(
    "btn btn-primary-white btn-medium !border-white hover:!border-0 font-medium border-0 md:ml-auto w-fit mx-auto md:relative bottom-xxl md:bottom-0",
  );
  const t = useTranslations();
  const locale = useLocale();

  const trakingHandler = (eventName: string) => {
    const newDate = new Date(eventDate);
    const date = newDate.toDateString();

    eventSectionTracking(eventName, date);
    ctaClickTracking(earlyAccessCta?.label, "EventLaunchCard", eventName);
  };

  return (
    <div className="flex flex-col countDown gap-xxl mt-xl md:mt-0 whitespace-nowrap md:w-fit md:flex-row md:gap-0 md:box-material-primary overflow-hidden text-center">
      {timeUntilEvent.days === 0 && timeUntilEvent.hours === 0 && timeUntilEvent.minutes === 0 ? (
        <>
          <div className="liveEvent mr-m">
            <span className="border-2 border-white rounded-full bg-status-error w-s h-s" /> {t("label.event.liveNow")}
          </div>
          {/* <Button
            href={getLink(earlyAccessCta, locale)}
            className={cn(EventLaunchButtonClasses, "text-center")}
            type="button"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.WHITE}
            onClick={() => {
              trakingHandler(eventName || "");
            }}
          >
            {t("label.event.haveLook")}
          </Button> */}
        </>
      ) : (
        <>
          <div className="liveEvent mr-s justify-center">
            <span className="inline-flex items-center h-full font-normal border-r pr-s border-r-gray-50 text-s">
              {timeUntilEvent.days}
              {t("letter.timeUntilEvent.days")}
            </span>
            <span className="inline-flex items-center h-full font-normal border-r pr-s border-r-gray-50 text-s">
              {timeUntilEvent.hours} {t("letter.timeUntilEvent.hours")}
            </span>
            <span className="inline-flex items-center h-full font-normal pr-xs text-s">
              {timeUntilEvent.minutes}
              {t("letter.timeUntilEvent.minutes")}
            </span>
          </div>
          {/* <Button
            href={getLink(earlyAccessCta, locale)}
            className={EventLaunchButtonClasses}
            type="button"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.WHITE}
            customClass="text-center"
            onClick={() => {
              trakingHandler(eventName || "");
            }}
          >
            {earlyAccessCta?.label}
          </Button> */}
        </>
      )}
    </div>
  );
};

const SalesEventLaunchCard: FC<EventLaunchCardProps> = ({ entry }) => {
  const isSo = useIsSaleScreen();
  const t = useTranslations();
  const inspectorMode = useInspectorMode(entry.id);
  const swiperRef = useRef<any>();
  const arrayBars: number[] = [];
  const [isPlay, setIsPlay] = useState<boolean>(true);
  const [whoPlying, setWhoPlaying] = useState<number>(0);

  const totalSlides = entry?.events?.length;

  entry.events?.forEach((i) => {
    arrayBars.push(0);
  });

  const [bars, setBars] = useState(arrayBars);
  const [thumbsSwiper, setThumbsSwiper] = useState<any | null>(null);

  const shoWHideLabel = (index: number) => {
    const els = document.querySelectorAll("div.label-thumb");

    if (els.length) {
      els.forEach((item) => {
        item.classList.remove("hidden");
      });
      els[index].classList.add("hidden");
    }
  };

  const changeSlide = useCallback((index: number) => {
    shoWHideLabel(index);
    swiperRef.current.swiper.slideTo(index);
  }, []);

  const handleThumbClick = (index: number) => {
    shoWHideLabel(index);
    if (whoPlying === index && isPlay) {
      setIsPlay(false);
    } else {
      setIsPlay(true);
      const newBars = [...bars];
      for (let i = 0; i < newBars.length; i++) {
        newBars[i] = 0;
      }
      newBars[index] = bars[index] + 1;
      setWhoPlaying(index);
      setBars(newBars);
    }
  };

  useEffect(() => {
    const updateBars = () => {
      const newBars = [...bars];
      for (let i = 0; i < newBars.length; i++) {
        if (newBars[i] !== 0) {
          newBars[i] > 0 ? newBars[i]++ : newBars[i];

          if (newBars[i] > 100) {
            newBars[i] = 0;
            setWhoPlaying(newBars[i]);

            const nextIndex = (i + 1) % totalSlides;
            newBars[nextIndex] = 1;

            changeSlide(nextIndex);
          }
        } else {
          setIsPlay(true);
        }
      }
      setBars(newBars);
    };
    const intervalId = setInterval(() => {
      isPlay ? updateBars() : null;
    }, 100); // Velocità

    // Pulisci l'intervallo quando il componente si dismonta
    return () => clearInterval(intervalId);
  }, [bars, changeSlide, isPlay, totalSlides]);

  if (!isSo) return null;

  // Define a function to handle setting the thumbsSwiper
  const handleSetThumbsSwiper = (swiper: any) => {
    setBars([1, 0, 0, 0]);
    shoWHideLabel(0);
    setThumbsSwiper(swiper);
  };

  function toAbsolute(url: string): string | import("next/dist/shared/lib/get-img-props").StaticImport {
    throw new Error("Function not implemented.");
  }

  return (
    <div className="flex justify-center">
      <div className="relative w-full">
        <div
          className="absolute left-0 right-0 z-20 mx-auto top-xxl w-fit mt-xxl md:top-0 md:bottom-0 md:m-auto md:h-fit md:left-m md:right-auto"
          {...inspectorMode.getProps("events")}
        >
          <Swiper
            className="thumbs"
            modules={[Thumbs]}
            onSwiper={handleSetThumbsSwiper}
            slidesPerView={entry.events?.length}
            direction="horizontal"
            pagination={false}
            breakpoints={{
              1024: {
                direction: "vertical",
              },
            }}
          >
            {entry.events &&
              entry.events.length > 1 &&
              entry.events.map((eventLaunchCard, index) => (
                <SwiperSlide key={index}>
                  <div className="circleStories" key={`thumb-${index}`} onClick={() => handleThumbClick(index)}>
                    <div className="backgroundCircleStories">
                      <CircularProgressbar
                        className={`absolute z-50 !w-[80%] md:!w-full`}
                        value={bars[index]}
                        strokeWidth={4}
                        styles={{
                          trail: {
                            strokeWidth: 4, // Imposta lo spessore del trail
                            stroke: "#cccccc",
                          },
                          path: {
                            stroke: "#ffffff", // Imposta il colore della parte che si riempe
                            transition: bars[index] === 0 ? "none" : "stroke-dashoffset 0.5s ease 0s",
                          },
                        }}
                      />
                      <span className="absolute">
                        {bars[index] !== 0 && <Icon name="pause" className="stroke-white" size="24" title="Pause" />}
                        {/* <Icon name={`${bars[index] === 0 ? "play" : "pause"}`} className="stroke-white" size="24" title="Alternative text" /> */}
                      </span>
                      <Image
                        className="rounded-full"
                        src={eventLaunchCard.circleImage.url}
                        alt={eventLaunchCard.circleImage.title || ""}
                        width={46}
                        height={46}
                      />
                    </div>
                  </div>
                  <div
                    className={`label-thumb max-w-[55px] max-h-[22px] font-secondary font-normal text-xs leading-3 text-white text-center overflow-hidden mx-auto my-[6px]`}
                  >
                    {eventLaunchCard.title}
                  </div>
                </SwiperSlide>
              ))}
          </Swiper>
        </div>

        <Swiper
          ref={swiperRef}
          spaceBetween={30}
          effect={"fade"}
          navigation={false}
          pagination={false}
          mousewheel={{
            enabled: true,
            releaseOnEdges: true,
          }}
          modules={[Autoplay, EffectFade, Thumbs]}
          thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
          className="mySwiper"
        >
          {entry.events?.map((eventLaunchCard, index) => {
            const poster = getImageProps({
              alt: "",
              width: 1024,
              height: 768,
              quality: 80,
              src: eventLaunchCard.backupVideoImage.url,
            });
            return (
              <SwiperSlide key={index}>
                <div className="relative">
                  <div className="absolute z-10 w-full h-full img-overlay-homepage-hero"></div>
                  <video
                    className="videoHero"
                    width={eventLaunchCard.video?.width}
                    height={eventLaunchCard.video?.height}
                    poster={poster.props.src}
                    autoPlay
                    muted
                    loop
                    playsInline
                  >
                    <source src={eventLaunchCard.video.url} type="video/mp4" />
                  </video>

                  <div className="absolute top-0 left-0 z-30 w-full h-full">
                    <div className="flex md:w-19\/24 flex-col items-center justify-center h-full md:flex-row mx-auto mt-[80px] md:mt-0">
                      <h3 className="md:hidden font-primary text-white uppercase text-center mb-l">{t("label.card.eventLaunch")}</h3>
                      <div className="flex justify-center w-2/3 md:w-6/12 mb-xs">
                        <Image src={eventLaunchCard.logo?.url} fit="pad" width={350} height={196} alt={eventLaunchCard.logo?.title} />
                      </div>

                      <div className="flex flex-col md:w-6/12 gap-l">
                        <h3 className="hidden md:font-primary md:inline md:text-white uppercase">{eventLaunchCard.title}</h3>

                        <div className="flex flex-col items-center text-white whitespace-nowrap md:flex-row">
                          <p className="flex items-center mb-xs md:mb-0">
                            <Icon title="date" name="calendar" size="20" className="stroke-white" />
                            <span className="ml-[6px] md:mr-l">{formatDate(eventLaunchCard.eventDate)}</span>
                          </p>
                          <p className="flex items-center">
                            <Icon title="position" name="geotag" size="20" className="stroke-white" />
                            <span className="ml-[6px]">{eventLaunchCard.eventLocation}</span>
                          </p>
                        </div>
                        <div className="hidden md:inline md:text-white pr-xl">
                          <p>{eventLaunchCard.description}</p>
                        </div>

                        <Countdown
                          eventDate={eventLaunchCard.eventDate}
                          eventName={eventLaunchCard.title}
                          earlyAccessCta={eventLaunchCard.applyForEarlyAccessCta}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}
        </Swiper>
      </div>
    </div>
  );
};

export default SalesEventLaunchCard;
